import axios, {
  AxiosInterceptorManager,
  AxiosRequestConfig,
  Method,
} from 'axios';
import * as Sentry from '@sentry/react';
import TypeChecker from 'helpers/classes/TypeChecker';
import eventEmitter, { EVENTS } from 'eventEmitter';
import {
  EXMINDS_API_ENTRY_ROUTE,
  EXMINDS_API_HOST,
  EXMINDS_API_PORT,
  EXMINDS_API_PROTOCOL,
} from 'helpers/constants/envVars';
// eslint-disable-next-line import/no-cycle
import { authRequest } from './AuthRequest';
import { AUTH_REFRESH_TOKENS_SUBROUTE, HttpStatusCode } from './constants';

export interface QueryParam {
  key: string;
  value: string;
}

export interface Pagination {
  total: number;
  limit: string;
  offset: string;
}

abstract class AbstractRequest {
  get apiProtocol(): string {
    return EXMINDS_API_PROTOCOL;
  }

  get apiHost(): string {
    return EXMINDS_API_HOST;
  }

  get apiPort(): string {
    return EXMINDS_API_PORT;
  }

  get apiEntryRoute(): string {
    return EXMINDS_API_ENTRY_ROUTE;
  }

  get apiUrl(): string {
    return `${this.apiBaseUrl}${this.apiEntryRoute}`;
  }

  get apiBaseUrl(): string {
    return `${this.apiProtocol}://${this.apiHost}${
      this.apiPort ? `:${this.apiPort}` : ''
    }`;
  }

  abstract get apiResource(): string;

  get apiRoute(): string {
    return `${this.apiUrl}/${this.apiResource}`;
  }

  abstract get routes(): Record<string, string>;

  async request(
    method: Method,
    url: string,
    data?: unknown,
    extraConfig?: AxiosRequestConfig
  ): Promise<any> {
    let result;
    try {
      result = await axios({ method, url, data, ...extraConfig });
    } catch (e: any) {
      const responseUrl = e?.request?.responseURL;
      const status = e?.request?.status;

      if (status === HttpStatusCode.BAD_REQUEST) {
        eventEmitter.emit(EVENTS.API.BAD_REQUEST);
      }
      if (
        TypeChecker.isString(responseUrl) &&
        e?.response?.status === HttpStatusCode.UNAUTHORIZED
      ) {
        if (responseUrl.endsWith(AUTH_REFRESH_TOKENS_SUBROUTE)) {
          eventEmitter.emit(EVENTS.API.UNAUTHORIZED_ACCESS_REQUEST);
        } else {
          return authRequest
            .refreshAccessToken()
            .then(() => this.request(method, url, data, extraConfig));
        }
      }
      Sentry.captureException(e);
      throw e;
    }
    return result;
  }

  getRequestInterceptors(): AxiosInterceptorManager<AxiosRequestConfig> {
    return axios.interceptors.request;
  }

  addRequestInterceptor(handler: (config: AxiosRequestConfig) => void): number {
    return this.getRequestInterceptors().use(handler);
  }

  removeRequestInterceptor(id: number): void {
    this.getRequestInterceptors().eject(id);
  }

  buildQueryParams(queryParams: QueryParam[]): string {
    let queryString = '';
    queryParams.forEach((queryParam) => {
      if (queryString) {
        queryString += `&${queryParam.key}=${queryParam.value}`;
      } else {
        queryString = `?${queryParam.key}=${queryParam.value}`;
      }
    });

    return queryString;
  }
}

export default AbstractRequest;
